import { z } from 'zod';

import { WatchlistSymbolSchema } from './marketHealth';
import type { IndustryLeadership, ScanWindow } from './marketScan';

export const MARKET_FLOW_UNIVERSES = ['THEME', 'SECTOR'] as const;
export const MarketFlowUniverseSchema = z.enum(MARKET_FLOW_UNIVERSES);
export type MarketFlowUniverse = z.infer<typeof MarketFlowUniverseSchema>;

export const EXTENDED_SESSIONS = ['PREMARKET', 'AFTER_HOURS'] as const;
export type ExtendedSession = (typeof EXTENDED_SESSIONS)[number];

export const FLOW_PERIODS = [
  'PREMARKET',
  'AFTER_HOURS',
  'INTRADAY',
  '1D',
  '1W',
  '1M',
  '3M',
  '6M',
  '1Y',
] as const;
export type FlowPeriod = (typeof FLOW_PERIODS)[number];

export const AddFlowEtfSchema = z.object({
  symbol: WatchlistSymbolSchema,
  universe: MarketFlowUniverseSchema,
  group: z.string().trim().min(1).max(60),
  description: z.string().trim().max(120).optional(),
});
export type AddFlowEtfInput = z.infer<typeof AddFlowEtfSchema>;

export interface FlowEtfDto {
  id: string;
  symbol: string;
  universe: MarketFlowUniverse;
  group: string;
  description: string | null;
  dataStatus: 'READY' | 'PENDING';
}

export interface FlowEtfListDto {
  etfs: FlowEtfDto[];
}

export interface FlowEtfMetricsDto {
  symbol: string;
  group: string;
  description: string | null;
  performance: number;
  percentile: number;
  intradayChangePct: number | null;
  oneDayChangePct: number | null;
  adrPct: number | null;
  atrPct: number | null;
  atrExtension: number;
  averageDollarVolume30d: number | null;
  nonExtended: boolean;
  isTight: boolean | null;
  coilSetup: string | null;
}

export interface FlowGroupDto {
  rank: number;
  group: string;
  score: number;
  medianPerformance: number;
  bestPerformance: number;
  confirmation: number;
  memberCount: number;
  nonExtendedMembers: number;
  leaderEtf: string;
  members: FlowEtfMetricsDto[];
}

export interface FlowRankingDto {
  universe: MarketFlowUniverse;
  top: number;
  windows: Record<ScanWindow, FlowGroupDto[]>;
}

export interface FlowLeadershipPointDto {
  date: string;
  scores: Record<string, number>;
}

export interface FlowReturnRowDto {
  key: string;
  group: string;
  values: Record<FlowPeriod, number | null>;
}

export interface FlowReturnsDto {
  groups: FlowReturnRowDto[];
  etfs: FlowReturnRowDto[];
  extendedCapturedAt: Record<ExtendedSession, string | null>;
}

export interface MarketFlowDto {
  status: 'READY' | 'NOT_AVAILABLE';
  sessionDate: string | null;
  themes: FlowRankingDto | null;
  sectors: FlowRankingDto | null;
  sectorLeadership: Record<ScanWindow, FlowLeadershipPointDto[]> | null;
  themeReturns: FlowReturnsDto | null;
  industryLeadership: { sessionDate: string; windows: IndustryLeadership } | null;
  pendingSymbols: string[];
  source: { name: string; url: string };
  informationalOnly: true;
}

export const EtfHoldingSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  weightPct: z.number(),
  averageDollarVolume30d: z.number().nullable(),
  atrExtension: z.number().nullable(),
});
export type EtfHolding = z.infer<typeof EtfHoldingSchema>;
export const EtfHoldingsSchema = z.array(EtfHoldingSchema);

export interface EtfHoldingsDto {
  symbol: string;
  asOf: string | null;
  statsAsOf: string | null;
  holdings: EtfHolding[];
}
